import classNames from 'classnames';
import React from 'react';
import { labelFromClickEvent, useAsyncButton } from 'ud-ui/hooks/use-async-button';

interface ActionButtonOwnProps<C extends React.ElementType> {
  /** Своя кнопка экрана (styled-компонент), которой кит даёт замок: вид — её, поведение — китовое. */
  as: C;
  /** Внешний спиннер: запрос идёт не из этой кнопки. Выключает кнопку, как у `UDButton`. */
  loading?: boolean;
  disabled?: boolean;
  /** По умолчанию `button`, как у `UDButton`: внутри `<form>` кнопка действия не отправляет форму. */
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  /** Узел или функция от «идёт запрос» — у своей кнопки спиннера нет, подпись меняет вызывающий. */
  children?: React.ReactNode | ((isBusy: boolean) => React.ReactNode);
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
}

export type UDActionButtonProps<C extends React.ElementType> = ActionButtonOwnProps<C> &
  Omit<React.ComponentPropsWithoutRef<C>, keyof ActionButtonOwnProps<C>>;

/**
 * Замок кита для кнопки со своим видом (IMGM-1561).
 *
 * Палитры джойстика трансляции, шапки медиа-редактора и редактора табло не ложатся на `UDButton`, но двойной клик
 * по их кнопкам должен запираться так же: промис `onClick` держит кнопку выключенной до ответа (`useAsyncButton`),
 * на время запроса — класс `loading` и `aria-busy`. Так хук не выходит за пределы кита.
 *
 * @example
 * <UDActionButton as={SaveButton} type="button" onClick={save} aria-label={t`Сохранить`}>
 *   {(isBusy) => (isBusy ? t`Сохранение…` : t`Сохранить`)}
 * </UDActionButton>
 */
function UDActionButton<C extends React.ElementType>(props: UDActionButtonProps<C>) {
  const { as: Component, loading, disabled, className, children, onClick, type = 'button', ...rest } = props;
  const { isLoading, handleClick } = useAsyncButton(onClick, {
    label: (event?: unknown) => labelFromClickEvent(event) ?? 'UDActionButton',
  });
  const isBusy = isLoading || Boolean(loading);
  // Полиморфный `as`: TS не сводит пропсы произвольного компонента — проверку типов держит `UDActionButtonProps`.
  const Tag = Component as React.ElementType;

  return (
    <Tag
      {...rest}
      type={type}
      className={classNames(className, { loading: isBusy })}
      // И по внешнему `loading`, как `UDButton`: иначе спиннер есть, а Enter / пробел на кнопке шлют повтор.
      disabled={disabled || isBusy}
      aria-busy={isBusy || undefined}
      onClick={handleClick}
    >
      {typeof children === 'function' ? children(isBusy) : children}
    </Tag>
  );
}

export default UDActionButton;
